import React, { useEffect, useCallback } from 'react';
import { Drawer, Form, Button, Space, Spin, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import StyleForm from './StyleForm';
import LoraConfigPanel from './LoraConfigPanel';
import {
  getStyleDetail,
  createStyle,
  updateStyle,
} from '../api';
import { StyleFormData, StyleRecord } from '../types';
import { QUERY_KEYS, DRAWER_CONFIG, FORM_DEFAULT_VALUES } from '../constants';
import { getFormInitialValues } from '../utils/formUtils';

interface StyleDrawerProps {
  open: boolean;
  editingStyle?: StyleRecord | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const StyleDrawer: React.FC<StyleDrawerProps> = ({
  open,
  editingStyle,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<StyleFormData>();
  const isEdit = !!editingStyle?.styleId;
  const styleId = editingStyle?.styleId;
  const supportLoraValue = Form.useWatch('supportLora', form);
  // 跟踪当前 form 里的 model（编辑已有风格时 detailData.model 是旧值，以 form 实时值为准）
  const currentModel = Form.useWatch('model', form);

  // 获取详情
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: [QUERY_KEYS.STYLE_DETAIL, styleId],
    queryFn: () => getStyleDetail(styleId!),
    enabled: isEdit && !!styleId && open,
  });

  // 以 form 当前值优先，fallback 到 detail API 原始值
  const effectiveModel = currentModel ?? detailData?.model;

  // 创建
  const createMutation = useMutation({
    mutationFn: createStyle,
    onSuccess: () => {
      message.success('创建成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STYLE_LIST] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      message.error(error.message || '创建失败');
    },
  });

  // 更新
  const updateMutation = useMutation({
    mutationFn: (data: StyleFormData) => updateStyle({ ...data, styleId: styleId! }),
    onSuccess: () => {
      message.success('更新成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STYLE_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STYLE_DETAIL, styleId] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      message.error(error.message || '更新失败');
    },
  });

  // 加载详情数据到表单
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (detailData && isEdit) {
      const initialValues = getFormInitialValues(detailData);
      form.resetFields();
      form.setFieldsValue(initialValues);
    } else {
      // 新增模式：使用默认值
      form.resetFields();
      form.setFieldsValue(FORM_DEFAULT_VALUES);
    }
  }, [detailData, editingStyle, isEdit, open, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        // freeUse: values.isVipStyle === 1 ? (values.freeUse ?? 0) : 0,
      };
      const mutation = isEdit ? updateMutation : createMutation;
      mutation.mutate(payload);
    } catch (error) {
      // 表单验证失败，不显示错误（Ant Design 会自动显示）
    }
  }, [form, isEdit, updateMutation, createMutation]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const loading = detailLoading || createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEdit ? '编辑文生图风格' : '新增文生图风格'}
      width={DRAWER_CONFIG.WIDTH}
      onClose={handleClose}
      open={open}
      placement={DRAWER_CONFIG.PLACEMENT}
      maskClosable={false}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <StyleForm form={form} />
        <LoraConfigPanel
          styleId={styleId}
          styleSource={(() => {
            const m = String(effectiveModel);
            // 每个 model 对应的 style_lora_config.style_source 值
            if (m === '20') return 'flux';                      // Runware Flux Klein (AIR ID)
            if (m === '21') return 'flux-pro';                  // Replicate Flux.1-dev (URL)
            if (m === '22') return 'runware-flux';              // Runware Flux.1-dev (AIR ID)
            if (m === '23' || m === '24') return 'runware-sdxl'; // Runware Illustrious/NoobAI 共享 (AIR ID)
            return detailData?.modelType === 2 ? 'sx2' : 'sx';  // SuppX ComfyUI
          })()}
          model={effectiveModel}
          supportLora={supportLoraValue}
        />
      </Spin>
    </Drawer>
  );
};

export default StyleDrawer;

